import { Database } from '../../../../../shared/database.enum';
import { CheckboxFilter } from '../../models/checkbox-filter';
import { DropdownFilter } from '../../models/dropdown-filter';
import { DropdownFilterItem } from '../../models/dropdown-filter-item';
import { BasicConstants } from './basic-constants.enum';
import { BasicFilter } from './basic-filter.enum';
import {
  BreederGroupValues,
  Breeds, ensure, RegisteredStatus, Sexes, WoolTypes,
} from './basic-utils-constants';

/**
 * Gets the available breeds.
 */
export const getBreeds = (database: Database)
  : DropdownFilterItem[] => Breeds.filter((b) => b.db === database);

/**
 * Gets the 'breeds' dropdown placeholder value based on the selected database.
 * @param database
 */
export const getBreedPlaceholder = (database: Database) => {
  switch (database) {
    case Database.TerminalShedders:
      return 'All Shedders';
    case Database.MaternalPrimeSAMMs:
      return 'Prime SAMM';
    case Database.Dohne:
      return 'Dohne Merino';
    case Database.Boer:
      return 'Goats';
      // *TEMA* Starts
    case Database.LambplanAll:
      return 'Lambplan';
      // *TEMA* End
    default:
      return 'All Breeds';
  }
};

/**
 * Returns a breed value if one exists in the filters, otherwise will return
 * the default value for Prime SAMM, Dohne or Boer.
 *
 * This is available because both Dohne and Boer dropdowns don't have any values
 * but the specific breed id is required for the API else other breeds for
 * those databases will be returned with the results.
 * @param database
 * @param filters
 */
export const getBreedOrDefault = (database: Database, filters: Map<BasicFilter, any>) => {
  const breeds = filters.get(BasicFilter.Breed);

  // If a breed exists in the filters, return it.
  if (breeds.length) {
    return breeds.map((b: any) => b.id).toString();
  }

  // If no breed was selected, get the default value for Prime SAMM, Dohne and Boer.
  switch (database) {
    case Database.MaternalPrimeSAMMs:
    case Database.Dohne:
    case Database.Boer:
      return getBreeds(database)[0].id;
    default:
      return '';
  }
};

/**
 * Gets the available birth years.
 */
export const getBirthYears = (): DropdownFilterItem[] => {
  const d = new Date();
  const years: DropdownFilterItem[] = [];
  while (d.getFullYear() >= 1987) {
    years.push({ id: d.getFullYear().toString(), value: d.getFullYear().toString() });
    d.setFullYear(d.getFullYear() - 1);
  }
  return years;
};

/**
 * Gets the available sexes.
 */
export const getSexes = (): DropdownFilterItem[] => Sexes;

/**
 * Gets the wool type filter values.
 */
export const getWoolTypes = (): DropdownFilterItem[] => WoolTypes;

/**
 * Gets the breeder group filter values.
 * @param database
 */
export const getBreederGroupValues = (database: Database): DropdownFilterItem[] => {
  switch (database) {
    case Database.TerminalAll:
      return ensure(BreederGroupValues.find((group) => group.breederGroup === Database.TerminalAll)).groups;
    case Database.MaternalAll:
    case Database.MaternalFirstCrossSire:
      return ensure(BreederGroupValues.find((group) => group.breederGroup === Database.MaternalAll)).groups;
    default:
      return [];
  }
};

/**
 * Gets the registered status filter values.
 */
export const getRegisteredStatus = (): DropdownFilterItem[] => RegisteredStatus;

/**
 * Gets the dropdown filters for the selected database.
 * @param database
 */
export const getDropdowns = (database: Database): DropdownFilter[] => {
  const breeds = getBreeds(database);
  const birthYears = getBirthYears();
  const sexes = getSexes();

  const dropdowns: DropdownFilter[] = [
    {
      key: BasicFilter.Breed,
      label: 'Breeds',
      placeholder: getBreedPlaceholder(database),
      values: breeds,
      multiple: true,
      disabled: breeds.length <= 1,
      db: [Database.All],
    },
    {
      key: BasicFilter.BirthYear,
      label: 'Birth Year',
      placeholder: 'Any year',
      values: birthYears,
      multiple: true,
      db: [Database.All],
    },
    {
      key: BasicFilter.Sex,
      label: 'Sex',
      values: sexes,
      db: [Database.All],
    },
    {
      key: BasicFilter.WoolType,
      label: 'Wool Types',
      placeholder: 'All types',
      values: getWoolTypes(),
      multiple: true,
      db: [Database.Merino],
    },
    {
      key: BasicFilter.BreederGroup,
      label: 'Breeder Group Member',
      values: getBreederGroupValues(database),
      db: [Database.TerminalAll, Database.MaternalAll, Database.MaternalFirstCrossSire],
    },
    {
      key: BasicFilter.RegisteredStatus,
      label: 'Registered Status',
      values: getRegisteredStatus(),
      db: [Database.Dohne],
    },
  ];

  return dropdowns.filter((d) => d.db!.includes(database) || d.db!.includes(Database.All));
};

/**
 * Gets the checkbox filters for the selected database.
 * @param database
 */
export const getCheckboxes = (database: Database): CheckboxFilter[] => {
  const checkboxes: CheckboxFilter[] = [
    {
      key: BasicFilter.ForSale,
      label: BasicConstants.ForSaleLabel,
      tooltip: BasicConstants.ForSaleTooltip,
      db: [
        Database.All,
      ],
    },
    {
      key: BasicFilter.SemenAvailable,
      label: BasicConstants.SemenAvailableLabel,
      tooltip: BasicConstants.SemenAvailableTooltip,
      db: [
        Database.All,
      ],
    },
    {
      key: BasicFilter.ProgenyInReferenceFlock,
      label: BasicConstants.ProgenyInReferenceFlockLabel,
      tooltip: BasicConstants.ProgenyInReferenceFlockTooltip,
      db: [
        Database.TerminalAll, Database.TerminalShedders,
        Database.MaternalAll, Database.MaternalFirstCrossSire,
        Database.MaternalCorriedale, Database.MaternalPrimeSAMMs,
        Database.Merino, Database.Dohne,
      ],
    },
    {
      key: BasicFilter.GenomeTested,
      label: BasicConstants.GenomeTestedLabel,
      tooltip: BasicConstants.GenomeTestedTooltip,
      db: [
        Database.TerminalAll, Database.TerminalShedders,
        Database.MaternalAll, Database.MaternalFirstCrossSire,
        Database.Merino,
      ],
    },
    {
      key: BasicFilter.MerinoSuperiorSire,
      label: BasicConstants.MerinoSuperiorSireLabel,
      tooltip: BasicConstants.MerinoSuperiorSireTooltip,
      db: [
        Database.Merino,
      ],
    },
    {
      key: BasicFilter.PollHornResult,
      label: BasicConstants.PollHornResultLabel,
      tooltip: BasicConstants.PollHornResultTooltip,
      db: [
        Database.Merino,
      ],
    },
    {
      key: BasicFilter.MerinoLifetimeProductivity,
      label: BasicConstants.MerinoLifetimeProductivityLabel,
      tooltip: BasicConstants.MerinoLifetimeProductivityTooltip,
      db: [
        Database.Merino,
      ],
    },
  ];

  return checkboxes.filter((c) => c.db!.includes(database) || c.db!.includes(Database.All));
};

/**
 * Gets the initial state for the `basic` filters.
 *
 * Important: BreederGroup and RegisteredStatus has a default value set so that the
 * correct dropdown value is selected by default, however this causes these values to
 * be applied to the querystring when searching. Further work is required to
 * prevent these values being applied without removing the default value.
 */
export const basicDefaultState = new Map<BasicFilter, any>([
  [BasicFilter.Breed, []],
  [BasicFilter.BirthYear, []],
  [BasicFilter.Sex, getSexes()[0]],
  [BasicFilter.WoolType, []],
  [BasicFilter.BreederGroup, { value: 'All groups' }],
  [BasicFilter.RegisteredStatus, { value: 'Any status' }],
  [BasicFilter.FlockId, []],
  [BasicFilter.ForSale, { value: false, label: BasicConstants.ForSaleLabel }],
  [BasicFilter.SemenAvailable, { value: false, label: BasicConstants.SemenAvailableLabel }],
  [BasicFilter.ProgenyInReferenceFlock, { value: false, label: BasicConstants.ProgenyInReferenceFlockLabel }],
  [BasicFilter.GenomeTested, { value: false, label: BasicConstants.GenomeTestedLabel }],
  [BasicFilter.MerinoSuperiorSire, { value: false, label: BasicConstants.MerinoSuperiorSireLabel }],
  [BasicFilter.PollHornResult, { value: false, label: BasicConstants.PollHornResultLabel }],
  [BasicFilter.MerinoLifetimeProductivity, { value: false, label: BasicConstants.MerinoLifetimeProductivityLabel }],
]);
